import React from 'react';
import { GuideAccessNotice } from '../../../../../interfaces/stay-guide';
import { t } from '../../../../onboarding/stay/stay-strings';
import { useGuideNav } from './guide-frame';
import { At, longDateIn, timeIn } from './guide-format';
import { Notice, NoticeText, PrimaryRouterLink } from './guide.styled';

const L = 'en' as const;

/**
 * WHY THE CODES ARE NOT HERE (plan S7.7) — on the hub, Arrival and
 * Essentials while `access` is null. The SERVER says why (access_notice);
 * this only words it:
 *
 *   checkin_incomplete → finish check-in (+ "Continue check-in" → /s/:token)
 *   not_yet            → when they appear, on the building's clock
 *   suspended          → not available; contact the host
 *   id_review          → IS0: an ID needs a look; the host confirms it first
 *
 * The preview never links to a guest page, so it has no button.
 */
export const AccessNotice: React.FC<{ notice: GuideAccessNotice | null; zone: string }> = ({ notice, zone }) => {
  const nav = useGuideNav();
  if (!notice) return null;

  if (notice.reason === 'checkin_incomplete') {
    return (
      <Notice $tone="brand" role="status" data-access-notice="checkin_incomplete">
        <NoticeText>{t(L, 'guide.noticeIncomplete')}</NoticeText>
        {!nav.preview && nav.token ? (
          <PrimaryRouterLink to={`/s/${nav.token}`}>{t(L, 'guide.continueCheckin')}</PrimaryRouterLink>
        ) : null}
      </Notice>
    );
  }

  if (notice.reason === 'id_review') {
    return (
      <Notice $tone="brand" role="status" data-access-notice="id_review">
        <NoticeText>{t(L, 'guide.noticeIdReview')}</NoticeText>
      </Notice>
    );
  }

  // IS9: a hotel stay waiting for its room — the codes wait for the room.
  if (notice.reason === 'room_pending') {
    return (
      <Notice $tone="brand" role="status" data-access-notice="room_pending">
        <NoticeText>{t(L, 'guide.noticeRoomPending')}</NoticeText>
      </Notice>
    );
  }

  if (notice.reason === 'suspended') {
    return (
      <Notice $tone="neutral" role="status" data-access-notice="suspended">
        <NoticeText>{t(L, 'guide.noticeSuspended')}</NoticeText>
      </Notice>
    );
  }

  // not_yet: "Door codes and Wi-Fi appear at 14:30 on Friday 24 October."
  const [before, middle, after] = t(L, 'guide.noticeNotYet').split(/\{time\}|\{date\}/);

  return (
    <Notice $tone="brand" role="status" data-access-notice="not_yet">
      <NoticeText>
        {before}
        <At iso={notice.reveal_at}>{timeIn(notice.reveal_at, zone)}</At>
        {middle}
        <At iso={notice.reveal_at}>{longDateIn(notice.reveal_at, zone)}</At>
        {after}
      </NoticeText>
    </Notice>
  );
};
